import { createModel } from '@rematch/core';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { setOrganizationToLocalStorage } from '../../utils/organization';
import { AxiosError } from 'axios';
import { Member } from './members';
import { Organization } from './organizations';
import { CognitoUserStatus } from '../../enums/cognito-user-status';

export type IUser = {
  id: string;
  email: string;
  password_digest: string | null;
  first_name: string;
  last_name: string;
  avatar: string | null;
  is_active: boolean | null;
  confirmed_at: Date | null;
  hidden_cycle_table_columns?: string[] | null;
  created_at?: Date;
  updated_at?: Date;
  members?: Member[];
  memberData?: Member;
  organization?: Organization;
  cognito_status?: CognitoUserStatus;
};

export type NewUserData = {
  first_name: string;
  last_name: string;
};

type UserState = {
  profile: IUser | null;
  selectedUser: IUser | null;
  isUserLoaded: boolean;
};

const DEFAULT_STATE: UserState = {
  profile: null,
  selectedUser: null,
  isUserLoaded: false,
};

export const user = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    setCurrent(state, user) {
      return {
        ...state,
        profile: user,
        isUserLoaded: true,
      };
    },
    setSelected(state, user) {
      return {
        ...state,
        selectedUser: user,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getCurrentUser(): Promise<IUser> {
        try {
          const response = await http(URL.CURRENT_USER);

          dispatch.user.setCurrent({
            ...response.data,
          });
          dispatch.members.set({
            currentUserMember: response.data?.memberData,
          });
          setOrganizationToLocalStorage(response.data?.organization.id);

          if (!response.data?.memberData?.active) {
            toast.warning(
              'Your account is currently disabled in this organization. Please contact the administrator to regain access.',
              { autoClose: 2000 },
            );
          }

          return response?.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateUserInfo(newData: NewUserData, state) {
        try {
          const result = await http.patch(URL.USER, newData);

          dispatch.user.setCurrent({
            ...state.user.profile,
            ...result.data,
          });
          toast.success('User data updated successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      // Silent on purpose - this saves in the background every time the user
      // toggles a column, so a toast per click would be noisy.
      async updateHiddenCycleTableColumns(hiddenColumns: string[], state) {
        try {
          const result = await http.patch(URL.USER, { hidden_cycle_table_columns: hiddenColumns });

          dispatch.user.setCurrent({
            ...state.user.profile,
            ...result.data,
          });
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      },

      async changeEmail(email) {
        try {
          const result = await http.post(URL.CHANGE_EMAIL, {
            email,
          });

          return result.status === 201;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }

          return false;
        }
      },

      async verifyEmail({ code, email }, state) {
        try {
          const result = await http.post(URL.VERIFY_EMAIL, {
            code,
            email,
          });

          dispatch.user.setCurrent({
            ...state.user.profile,
            email,
          });

          if (result.status === 201) {
            toast.success('Email updated successfully');
            return true;
          }
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getUserById(id) {
        try {
          const response = await http.get(generatePath(URL.USER_PROFILE, { id }));
          dispatch.user.setSelected(response.data);
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async changePassword({ currentPassword, newPassword }) {
        try {
          await http.post(URL.USER_CHANGE_PASSWORD, {
            currentPassword,
            newPassword,
          });

          toast.success('New password updated successfully');
          return true;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },
    };
  },
});
